import {
	Component,
	Input,
	OnInit,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatterFileTypeStrings, MortgageSupTypeStrings } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { MortgageDocumentTypeTag, MortgageTag } from '@core/util/tags';
import {
	MatterDocumentType,
	MatterFile,
	MortgageDocumentSubType,
} from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterUploadedFilesDispatch } from '@state/matter/uploaded-files/matter-uploaded-files.dispatch';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UploadedDeleteModalComponent } from 'src/app/private/sale/documents/uploaded/delete-modal/delete-modal.component';
import {
	UploadDocumentModalComponent,
	UploadModalDocumentData,
} from 'src/app/private/sale/documents/uploaded/modal/modal.component';
import { SelectionGroup } from '../../atomic/atoms/dropdown-options/dropdown-options.component';
import { TestHooks } from './multiple-url-pdf-viewer.hooks';

interface SelectedDocumentKebabOption {
	action: 'upload' | 'edit' | 'delete';
	classes?: string;
	icon: ICON;
	name: string;
}

@UntilDestroy()
@Component({
	selector: 'app-multiple-url-pdf-viewer',
	templateUrl: './multiple-url-pdf-viewer.component.html',
	standalone: false,
})
export class MultipleUrlPdfViewerComponent implements OnInit {
	testHooks = TestHooks;
	@Input() matterDocumentType: MatterDocumentType;
	@Input() contextId: string;
	@Input() highlightFields: any[];
	@Input() scrollToFieldName: string;

	public documents: MatterFile[];
	public selectedDocument: MatterFile;
	public ICON = ICON;
	public showDropdown = false;
	public itemGroups: SelectionGroup[] = [];
	public dropdownHeading = 'Documents';
	public SelectedDocumentKebabOptions: SelectedDocumentKebabOption[] = [
		{
			action: 'upload',
			classes: 'text-green',
			icon: ICON.ARROW_UP_FROM_BRACKET,
			name: 'Upload new',
		},
		{
			action: 'delete',
			classes: 'text-red',
			icon: ICON.DELETE,
			name: 'Delete document',
		},
	];

	@SelectSnapshot(SelectedMatter.id) matterId: string;

	fileDropdownShowing$ = new BehaviorSubject(false);

	@ViewChild('documentTemplate', { static: true })
	documentTemplate: TemplateRef<unknown>;

	constructor(
		private matterUploadedFilesDispatch: MatterUploadedFilesDispatch,
		private store: Store,
		public dialog: MatDialog,
		private snackbarService: SnackbarService,
	) {}

	ngOnInit(): void {
		this.store
			.select(
				MatterUploadedFilesSelectors.getUploadedFilesByType(
					this.matterDocumentType,
					this.getSystemTags(),
				),
			)
			.pipe(untilDestroyed(this))
			.subscribe((documents) => {
				if (documents?.length > 0) {
					this.documents = documents;
					this.dropdownHeading = `${MatterFileTypeStrings[this.matterDocumentType]} (${this.documents.length})`;
					this.selectDocument(this.documents[0]);
					this.createItemGroups(this.documents);
				}
			});
	}

	selectDocument(document: MatterFile): void {
		this.selectedDocument = document;
	}

	deleteDocument(documentId: string, documentName: string): void {
		this.dialog
			.open(UploadedDeleteModalComponent, {
				data: { documentName },
				maxWidth: '500px',
			})
			.afterClosed()
			.pipe(take(1), untilDestroyed(this))
			.subscribe((result) => {
				if (result) {
					this.matterUploadedFilesDispatch.Delete(documentId);
					this.snackbarService.openSnackBar({
						text: `${documentName} deleted.`,
						type: 'info',
					});
				}
			});
	}

	getSystemTags(): string[] {
		switch (this.matterDocumentType) {
			case MatterDocumentType.MORTGAGE:
				return [MortgageTag(this.contextId)];
			default:
				return [];
		}
	}

	uploadDocument(): void {
		const data: UploadModalDocumentData = {
			matterId: this.matterId,
			defaultFileType: this.matterDocumentType,
			lockFileType: true,
			systemTags: this.getSystemTags(),
			runAiScraping: false,
		};

		this.dialog.open(UploadDocumentModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	public onKebabSelect(
		doc: MatterFile,
		event: SelectedDocumentKebabOption,
	): void {
		this.showDropdown = false;
		switch (event.action) {
			case 'upload':
				this.uploadDocument();
				break;
			case 'edit':
				break;
			case 'delete':
				this.deleteDocument(doc.id, doc.name);
				break;
		}
	}

	toggleDropdown(): void {
		this.showDropdown = !this.showDropdown;
	}

	onSelect(event): void {
		this.toggleDropdown();
		this.selectDocument(event.result);
	}

	createItemGroups = (documents: MatterFile[]): void => {
		if (this.matterDocumentType === MatterDocumentType.MORTGAGE) {
			const newItemGroups: SelectionGroup[] = [];
			Object.values(MortgageDocumentSubType).forEach(
				(supType: MortgageDocumentSubType) => {
					const docs = documents.filter((doc) =>
						doc.systemTags.includes(MortgageDocumentTypeTag(supType)),
					);
					if (docs.length > 0) {
						newItemGroups.push({
							items: docs,
							title: `${MortgageSupTypeStrings[supType]} (${docs.length})`,
							template: this.documentTemplate,
						});
					}
				},
			);

			this.itemGroups = [...newItemGroups];

			return;
		}

		this.itemGroups = [
			{
				items: documents,
				template: this.documentTemplate,
				title: MatterFileTypeStrings[this.matterDocumentType],
			},
		];
	};
}
